import React, { useContext, AriaAttributes } from 'react';
import classnames from 'classnames';
import get from 'lodash/get';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { PopperDropdownToggle } from '../../../PopperDropdownLayout';
import { DropdownOption } from '../../../types';
import { DropdownStateContext } from '../../../DropdownStateManager';
import { IconSize } from '../../../../Icons/IconWrap';
import ArrowDownIcon from '../../../../Icons/ArrowDown';
import s from './StandardDropdownToggle.scss';

export interface Props {
  dropdownId: string;
  selectedItem?: DropdownOption;
  placeholder?: React.ReactNode;
  arrowSize?: IconSize;
  arrowClassName?: string;
  placeholderClassName?: string;
  toggleClassName?: string;
  openClassName?: string;
  'aria-label'?: AriaAttributes['aria-label'];
  'aria-haspopup'?: AriaAttributes['aria-haspopup'];
  tabIndex?: number;
  renderArrowDown?: (className: string) => React.ReactNode;
  onOpenBiData?: BiEvent;
  onCloseBiData?: BiEvent;
}

const StandardDropdownToggle: React.FC<React.PropsWithChildren<Props>> = ({
  dropdownId,
  selectedItem,
  placeholder,
  placeholderClassName,
  toggleClassName,
  openClassName,
  arrowClassName,
  arrowSize,
  renderArrowDown,
  'aria-label': ariaLabel,
  'aria-haspopup': ariaHasPopup,
  tabIndex,
  onOpenBiData,
  onCloseBiData,
}) => {
  const { isOpen } = useContext(DropdownStateContext);
  const selectedLabel = get(selectedItem, 'label');
  return (
    <PopperDropdownToggle
      className={classnames(
        s['toggle'],
        toggleClassName,
        !selectedLabel && placeholderClassName,
        isOpen && openClassName
      )}
      aria-label={ariaLabel}
      aria-expanded={isOpen}
      aria-controls={dropdownId}
      aria-haspopup={ariaHasPopup}
      tabIndex={tabIndex}
      onOpenBiData={onOpenBiData}
      onCloseBiData={onCloseBiData}
    >
      {get(selectedItem, 'icon', '')} {selectedLabel || placeholder}{' '}
      {renderArrowDown ? (
        renderArrowDown(
          classnames(s['arrow-icon'], arrowClassName, isOpen && s['is-open'])
        )
      ) : (
        <ArrowDownIcon
          className={classnames(
            s['arrow-icon'],
            arrowClassName,
            isOpen && s['is-open']
          )}
          size={arrowSize}
          disableDefaultColors
        />
      )}
    </PopperDropdownToggle>
  );
};
StandardDropdownToggle.displayName = 'StandardDropdownToggle';

export default StandardDropdownToggle;
